import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    List,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    ListItem,
    ListItemText,
    IconButton,
} from '@mui/material';
import {
    Edit,
    Delete,
    Add,
} from '@mui/icons-material';

const SystemPrompts = ({ initialPrompts, onSave }) => {
    const { t } = useTranslation();
    const [systemPrompts, setSystemPrompts] = useState([]);
    const [promptDialogOpen, setPromptDialogOpen] = useState(false);
    const [editingPromptIndex, setEditingPromptIndex] = useState(null);
    const [currentPrompt, setCurrentPrompt] = useState({ name: '', content: '' });

    useEffect(() => {
        if (initialPrompts) {
            setSystemPrompts(initialPrompts);
        }
    }, [initialPrompts]);

    const handleAddPrompt = () => {
        setCurrentPrompt({ name: '', content: '' });
        setEditingPromptIndex(null);
        setPromptDialogOpen(true);
    };

    const handleEditPrompt = (index) => {
        setCurrentPrompt({ ...systemPrompts[index] });
        setEditingPromptIndex(index);
        setPromptDialogOpen(true);
    };

    const handleDeletePrompt = (index) => {
        const updatedPrompts = [...systemPrompts];
        updatedPrompts.splice(index, 1);
        setSystemPrompts(updatedPrompts);
        onSave({ systemPrompts: updatedPrompts });
    };

    const handlePromptDialogClose = () => {
        setPromptDialogOpen(false);
    };

    const handleSavePrompt = () => {
        if (!currentPrompt.name.trim() || !currentPrompt.content.trim()) {
            return;
        }

        const updatedPrompts = [...systemPrompts];
        if (editingPromptIndex !== null) {
            updatedPrompts[editingPromptIndex] = currentPrompt;
        } else {
            updatedPrompts.push(currentPrompt);
        }

        setSystemPrompts(updatedPrompts);
        onSave({ systemPrompts: updatedPrompts });
        setPromptDialogOpen(false);
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {t('settings.systemPrompts.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
                {t('settings.systemPrompts.description')}
            </Typography>
            {/* System prompts list - Updated styling */}
            <List sx={{ width: '100%', mb: 2 }}>
                {systemPrompts.map((prompt, index) => (
                    <Paper
                        key={index}
                        elevation={0}
                        sx={{
                            mb: 2,
                            backgroundColor: theme => theme.palette.mode === 'dark'
                                ? theme.palette.grey[800]
                                : theme.palette.background.paper,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            overflow: 'hidden'
                        }}
                    >
                        <ListItem
                            secondaryAction={
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <IconButton edge="end" onClick={() => handleEditPrompt(index)}>
                                        <Edit />
                                    </IconButton>
                                    <IconButton edge="end" onClick={() => handleDeletePrompt(index)}>
                                        <Delete />
                                    </IconButton>
                                </Box>
                            }
                            sx={{
                                py: 1.5,
                                '&:hover': {
                                    backgroundColor: 'action.hover',
                                }
                            }}
                        >
                            <ListItemText
                                primary={
                                    <Typography variant="subtitle2" color="text.primary">
                                        {prompt.name}
                                    </Typography>
                                }
                                secondary={
                                    <Typography
                                        component="span"
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            display: 'block',
                                            mt: 0.5,
                                            maxWidth: '100%',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {prompt.content}
                                    </Typography>
                                }
                            />
                        </ListItem>
                    </Paper>
                ))}
            </List>

            {/* Add new prompt button */}
            <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={handleAddPrompt}
                sx={{ mt: 2 }}
            >
                {t('settings.systemPrompts.addNew')}
            </Button>

            {/* Add/Edit Prompt Dialog */}
            <Dialog
                open={promptDialogOpen}
                onClose={handlePromptDialogClose}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>
                    {editingPromptIndex !== null ? t('settings.systemPrompts.editPrompt') : t('settings.systemPrompts.addPrompt')}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="prompt-name"
                        label={t('settings.systemPrompts.promptName')}
                        fullWidth
                        value={currentPrompt.name}
                        onChange={(e) => setCurrentPrompt({ ...currentPrompt, name: e.target.value.slice(0, 20) })}
                        inputProps={{ maxLength: 20 }}
                        helperText={`${currentPrompt.name.length}/20`}
                    />
                    <TextField
                        margin="dense"
                        id="prompt-content"
                        label={t('settings.systemPrompts.promptContent')}
                        fullWidth
                        multiline
                        rows={10}
                        value={currentPrompt.content}
                        onChange={(e) => setCurrentPrompt({ ...currentPrompt, content: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handlePromptDialogClose}>{t('cancel')}</Button>
                    <Button onClick={handleSavePrompt}>{t('save')}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SystemPrompts;